// Cliente HTTP — ampliado con todos los endpoints del backend.
import axios from 'axios';
import { getMeta, setMeta, META } from '@/lib/db';

const BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Timeout ampliado: el drenado rápido de la cola de sync (Fix 2) envía
// batches de hasta 200 operaciones por request — en señal rural lenta un
// timeout corto cortaría el push a mitad de camino sin necesidad.
const http = axios.create({ baseURL: BASE, timeout: 60000 });

http.interceptors.request.use(async (config) => {
  const token = await getMeta(META.AUTH_TOKEN);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------- AUTH ----------
export async function register({ email, password, name, company }) {
  const r = await http.post('/auth/register', { email, password, name, company });
  await setMeta(META.AUTH_TOKEN, r.data.token);
  await setMeta(META.CURRENT_USER, r.data.user);
  return r.data;
}
export async function login({ email, password }) {
  const r = await http.post('/auth/login', { email, password });
  await setMeta(META.AUTH_TOKEN, r.data.token);
  await setMeta(META.CURRENT_USER, r.data.user);
  return r.data;
}
// Fix I (2026-09-17): renovación transparente del token — reemplaza SOLO el
// token y el usuario en meta. Nunca toca tablas locales ni la cola.
export async function refreshSession() {
  const r = await http.post('/auth/refresh');
  await setMeta(META.AUTH_TOKEN, r.data.token);
  await setMeta(META.CURRENT_USER, r.data.user);
  return r.data;
}
// Fix I: re-autenticación en el lugar (sesión vencida). Pide el token y lo
// DEVUELVE sin escribir nada: quien llama decide qué hacer (ver auth.jsx
// reauth) — así un login con otra cuenta no puede disparar ninguna limpieza
// antes de la confirmación explícita del usuario.
export async function reauthRequest({ email, password }) {
  return (await http.post('/auth/login', { email, password })).data;
}
export async function me() { return (await http.get('/auth/me')).data; }
export async function changePassword(payload) { return (await http.post('/auth/change-password', payload)).data; }
export async function inviteHandler(payload) { return (await http.post('/auth/invite-handler', payload)).data; }
export async function createHandler(payload) { return (await http.post('/auth/create-handler', payload)).data; }
export async function listHandlers() { return (await http.get('/auth/handlers')).data; }
export async function updateHandler(id, payload) { return (await http.put(`/auth/handlers/${id}`, payload)).data; }
export async function deleteHandler(id) { return (await http.delete(`/auth/handlers/${id}`)).data; } // Fix P
export async function listInvitations() { return (await http.get('/auth/invitations')).data; }
export async function acceptInvite(payload) {
  const r = await http.post('/auth/accept-invite', payload);
  await setMeta(META.AUTH_TOKEN, r.data.token);
  await setMeta(META.CURRENT_USER, r.data.user);
  return r.data;
}

// ---------- FARMS / LOTS ----------
export async function listFarms() { return (await http.get('/farms')).data; }
export async function createFarm(payload) { return (await http.post('/farms', payload)).data; }
export async function getFarm(id) { return (await http.get(`/farms/${id}`)).data; }
export async function updateFarm(id, payload) { return (await http.put(`/farms/${id}`, payload)).data; }
export async function deleteFarm(id) { return (await http.delete(`/farms/${id}`)).data; }
export async function listLots(farmId) { return (await http.get(`/farms/${farmId}/lots`)).data; }
export async function createLot(farmId, payload) { return (await http.post(`/farms/${farmId}/lots`, payload)).data; }
export async function updateLot(id, payload) { return (await http.put(`/lots/${id}`, payload)).data; }
export async function deleteLot(id) { return (await http.delete(`/lots/${id}`)).data; }

// ---------- ANIMALS ----------
export async function listAnimals(params = {}) { return (await http.get('/animals', { params })).data; }
export async function createAnimal(payload) { return (await http.post('/animals', payload)).data; }
export async function bulkInitialInventory(payload) { return (await http.post('/animals/bulk-initial', payload)).data; }
export async function registerBirth(payload) { return (await http.post('/animals/birth', payload)).data; }
export async function getAnimal(id) { return (await http.get(`/animals/${id}`)).data; }
export async function updateAnimal(id, payload) { return (await http.put(`/animals/${id}`, payload)).data; }
export async function deleteAnimal(id) { return (await http.delete(`/animals/${id}`)).data; }
export async function findAnimalByChip(rfid) { return (await http.get(`/animals/by-chip/${encodeURIComponent(rfid)}`)).data; }
export async function updateHealthFlag(id, is_sick) { return (await http.patch(`/animals/${id}/health`, { is_sick })).data; }

// ---------- PAPELERA (Tanda 2 — Blindaje de datos) ----------
export async function listTrash() { return (await http.get('/animals/trash')).data; }
export async function restoreAnimal(id) { return (await http.post(`/animals/${id}/restore`)).data; }
export async function restoreAnimalsBulk(animal_ids) { return (await http.post('/animals/restore-bulk', { animal_ids })).data; }
export async function bulkDeleteAnimals(animal_ids, confirm = false) {
  return (await http.post('/animals/bulk-delete', { animal_ids, confirm })).data;
}

// ---------- ANIMAL CHIPS (múltiples chips por animal) ----------
export async function linkChip(animalId, payload) {
  return (await http.post(`/animals/${animalId}/chips`, payload)).data;
}
export async function updateChip(animalId, rfid, payload) {
  return (await http.patch(`/animals/${animalId}/chips/${encodeURIComponent(rfid)}`, payload)).data;
}
export async function unlinkChip(animalId, rfid) {
  return (await http.delete(`/animals/${animalId}/chips/${encodeURIComponent(rfid)}`)).data;
}

// ---------- EVENTS ----------
export async function listEvents(animalId) { return (await http.get(`/animals/${animalId}/events`)).data; }
export async function createEvent(animalId, payload) { return (await http.post(`/animals/${animalId}/events`, payload)).data; }
export async function deleteEvent(id) { return (await http.delete(`/events/${id}`)).data; }

// ---------- ACTIONS (motor de plantillas 2-16) ----------
export async function submitAction(payload) { return (await http.post('/actions/submit', payload)).data; }
export async function listAlerts() { return (await http.get('/actions/alerts')).data; }

// ---------- INVENTORIES ----------
export async function listInventories(farmId) { return (await http.get('/inventories', { params: farmId ? { farm_id: farmId } : {} })).data; }
export async function startInventory(payload) { return (await http.post('/inventories', payload)).data; }
export async function getInventory(id) { return (await http.get(`/inventories/${id}`)).data; }
export async function scanChip(id, payload) { return (await http.post(`/inventories/${id}/scan`, payload)).data; }
export async function finishInventory(id, payload) { return (await http.post(`/inventories/${id}/finish`, payload)).data; }
export async function cancelInventory(id) { return (await http.post(`/inventories/${id}/cancel`)).data; }

// ---------- SYNC ----------
// Fix H (2026-09-17): un lote con fotos de hierro puede tardar más que el resto de
// las llamadas en una red de finca lenta — 120s de margen solo para este envío,
// para no abortarlo justo antes de terminar (el servidor SÍ lo aplica, pero el
// cliente lo daría por fallido y reintentaría desde cero).
export async function syncPush({ deviceId, operations }) {
  return (await http.post('/sync/push', { device_id: deviceId, operations }, { timeout: 120000 })).data;
}
export async function syncPull({ since } = {}) {
  // Fix N (2026-10-06): lazy_photos=1 -> las fotos de hierro solo viajan cuando
  // cambian (eran el 95 % de cada descarga). 120 s de margen, igual que el push.
  const params = { lazy_photos: 1, ...(since ? { since } : {}) };
  return (await http.get('/sync/pull', { params, timeout: 120000 })).data;
}
export async function listSyncLogs(params = {}) { return (await http.get('/sync/logs', { params })).data; }
export async function reconcileSummary() { return (await http.get('/sync/reconcile-summary')).data; }
export async function reconcileIds(entity) { return (await http.get('/sync/reconcile-ids', { params: { entity } })).data; }

// ---------- AI ----------
export async function aiPalpationReport(payload) {
  return (await http.post('/ai/palpation-report', payload)).data;
}

// ---------- DASHBOARD ----------
export async function getDashboard(farmId) {
  return (await http.get('/dashboard', { params: farmId ? { farm_id: farmId } : {} })).data;
}

// ---------- BACKUPS ----------
export async function listBackups() { return (await http.get('/backups')).data; }
export async function runBackup() { return (await http.post('/backups/run')).data; }
export async function backupHealth() { return (await http.get('/backups/health')).data; }
export async function preOpBackup(label) { return (await http.post('/backups/pre-op', { label })).data; }
export async function restoreBackup(id) { return (await http.post(`/backups/${id}/restore`, { confirm: true })).data; }
export function backupDownloadUrl(id) { return `${BASE}/backups/${id}/download`; }
export function backupDownloadNowUrl() { return `${BASE}/backups/download-now`; }
// Fix D (2026-08-24): reinicio del sistema — solo dueño, requiere confirm_text="REINICIAR".
export async function systemReset(confirmText) { return (await http.post('/reset/all', { confirm_text: confirmText })).data; }

// Fix F (2026-09-01): recategorización masiva por nuevos rangos de edad — solo dueño.
export async function recategorizePreview(includeManual = false) {
  return (await http.get('/animals/recategorize/preview', { params: { include_manual: includeManual } })).data;
}
export async function recategorizeApply(confirm = true, includeManual = false) {
  return (await http.post('/animals/recategorize/apply', { confirm, include_manual: includeManual })).data;
}

// ---------- MIGRACIÓN DE CUENTA ----------
export function migrationExportUrl() { return `${BASE}/migration/export`; }
export async function migrationExportPreview() { return (await http.get('/migration/export/preview')).data; }
export async function migrationImportPreview(file) {
  const form = new FormData();
  form.append('file', file);
  return (await http.post('/migration/import/preview', form)).data;
}
export async function migrationImport(file, { mode = 'replace', confirm = true, includeUsers = true } = {}) {
  const form = new FormData();
  form.append('file', file);
  form.append('confirm', confirm);
  form.append('mode', mode);
  form.append('include_users', includeUsers);
  return (await http.post('/migration/import', form)).data;
}

// ---------- EXPORT ----------
// Fix J (2026-09-17): filtros opcionales (finca, lote, estado). Solo se
// agregan a la URL cuando tienen valor — sin filtros, la descarga es
// exactamente la de siempre.
export function exportUrl(entity, format, { farmId, lotId, status } = {}) {
  const params = new URLSearchParams({ format });
  if (farmId) params.set('farm_id', farmId);
  if (lotId) params.set('lot_id', lotId);
  if (status) params.set('status', status);
  return `${BASE}/export/${entity}?${params.toString()}`;
}

export async function ping() { return (await http.get('/health')).data; }

export default http;
